import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "@legends/ui";
import {
  ApiError,
  fetchCaveLegend,
  fetchCaveLive,
  fetchMyTickets,
  fetchAuthz,
  fetchPublicCaveEvents,
  fetchPublicCaveTickets,
  fetchPublicLobby,
  logout,
  startTicketCheckout,
  track,
  type CaveEvent,
  type Legend,
  type LiveBroadcast,
  type PublicLobby,
  type TicketType,
} from "./api";
import ConsumerWatch from "./ConsumerWatch";
import LegendCave from "./LegendCave";
import LobbyRenderer from "./LobbyRenderer";
import RecordingsOnDemand from "./RecordingsOnDemand";
import { CheckoutReturnBanner, consumeBuyIntent, currentReturnPath, useCheckoutReturn } from "./checkoutReturn";
import { PersonaCard } from "./PersonaScreen";
import {
  AsyncState,
  formatDateTime,
  formatKind,
  formatPrice,
  isProrated,
  proratedNote,
  LegendsBrand,
  monoLabelStyle,
  rowStyle,
  type LoadStatus,
} from "./sections";

export interface CavePublicScreenProps {
  /** The cave's display name (or the tenant id as a generic fallback). */
  name: string;
  /** The cave id; when set, upcoming events + tickets are fetched. */
  caveId?: string;
  /** When set (anonymous), the public SIGN IN link renders upper-right. An
   *  optional ticket id carries the pending purchase through sign-in (ADR 0103). */
  onSignIn?: (buyTicketTypeId?: string) => void;
  /** When set (a signed-in fan), a Sign out button renders in the card header
   *  instead of the SIGN IN link. */
  onLogout?: () => void;
  /** When set (entered from the Selector), a "◂ Legends" back control renders in
   *  the Legend Cave header. */
  onBack?: () => void;
  /** The signed-in fan holds VIP all-access (LSC-549 / ADR 0236): the storefront
   *  reflects access (tickets show "VIP access ✓", no Buy nag) since a VIP watches
   *  any ticketed live CaveCast + eligible replay without a holding. */
  vip?: boolean;
}

const LIVE_POLL_MS = 10_000;
/** Cap on the storefront-identity hold (LSC-568): if the lobby/legend reads hang,
 *  stop showing the loading state and fall through to the legacy storefront. */
export const IDENTITY_TIMEOUT_MS = 5_000;

const mutedMonoStyle = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-2xs)",
  letterSpacing: "var(--ls-label)",
  color: "var(--text-dim)",
} as const;

/** A cave's public storefront — upcoming events, live programs + buy (ADR 0008/0012). */
export default function CavePublicScreen({ name, caveId, onSignIn, onLogout, onBack, vip = false }: CavePublicScreenProps) {
  const [events, setEvents] = useState<CaveEvent[]>([]);
  // Events are a launch-critical storefront read (ADR 0049): a failed fetch shows
  // an error + retry, not a silent "no upcoming events".
  const [eventsStatus, setEventsStatus] = useState<LoadStatus>("loading");
  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [live, setLive] = useState<LiveBroadcast[]>([]);
  const [owned, setOwned] = useState<Set<string>>(new Set());
  const [acquired, setAcquired] = useState<Set<string>>(new Set());
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  // VIP all-access (LSC-549 / ADR 0236): self-fetched so EVERY mount of this storefront
  // (cave host, apex legend deep-link, impersonation view) reflects access, not just the
  // one App passes `vip` to. The prop (when provided) wins as an override.
  const [vipState, setVipState] = useState(false);
  const isVip = vip || vipState;
  const [busyId, setBusyId] = useState<string | null>(null);
  const [watching, setWatching] = useState<string | null>(null);
  const [legend, setLegend] = useState<Legend | null>(null);
  // The published Cave Lobby (ADR 0130), best-effort. When a cave has PUBLISHED a
  // custom lobby, its document renders via LobbyRenderer; otherwise we retain the
  // legacy LegendCave / storefront fallback (spec §8) — so a failed/absent lobby
  // read never blocks the storefront, and the default-from-profile case keeps the
  // familiar render until an editor publishes.
  const [lobby, setLobby] = useState<PublicLobby | null>(null);
  // Which storefront branch renders (published lobby / Legend Cave / basic) depends on
  // the lobby + legend reads, so hold an intentional loading state until BOTH settle
  // (LSC-568) — otherwise the basic ticketing storefront flashes before the authored
  // Cave swaps in. Keyed by caveId so a cave change re-gates; a hung read is capped by
  // IDENTITY_TIMEOUT_MS and then falls through to the legacy render (never blocks).
  const [lobbySettled, setLobbySettled] = useState<string | null>(null);
  const [legendSettled, setLegendSettled] = useState<string | null>(null);
  const [identityTimedOut, setIdentityTimedOut] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const ticketsRef = useRef<HTMLElement | null>(null);

  // Post-checkout return (ADR 0048): the Stripe return resolves to a clear
  // success / finalizing / cancelled state; a success push-updates owned.
  const checkout = useCheckoutReturn((next) => {
    if (mountedRef.current) setOwned(next);
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadEvents = useCallback(() => {
    if (!caveId) return;
    setEventsStatus("loading");
    fetchPublicCaveEvents(caveId)
      .then((e) => {
        if (!mountedRef.current) return;
        setEvents(e);
        setEventsStatus("ready");
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setEvents([]);
        setEventsStatus("error");
      });
  }, [caveId]);

  useEffect(() => {
    if (!caveId) return;
    track("cave_viewed", { caveId });
    loadEvents();
    fetchPublicCaveTickets(caveId)
      .then(setTickets)
      .catch(() => setTickets([]));
    // `current` drops a stale read from a previous caveId so it can't paint the
    // wrong Cave after a cave change.
    let current = true;
    const fresh = () => current && mountedRef.current;
    setLegend(null);
    setLobby(null);
    fetchCaveLegend(caveId)
      .then((l) => fresh() && setLegend(l))
      .catch(() => undefined)
      .finally(() => fresh() && setLegendSettled(caveId));
    // Best-effort: a published lobby takes over the storefront; any failure
    // leaves `lobby` null and we keep the legacy render.
    fetchPublicLobby(caveId)
      .then((r) => fresh() && setLobby(r))
      .catch(() => undefined)
      .finally(() => fresh() && setLobbySettled(caveId));
    const timer = window.setTimeout(() => fresh() && setIdentityTimedOut(caveId), IDENTITY_TIMEOUT_MS);
    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [caveId, loadEvents]);

  // What the viewer holds — also our "is this session signed in?" probe. A 401
  // (or any failure) means treat them as anonymous; Buy becomes Sign in.
  useEffect(() => {
    fetchMyTickets()
      .then((held) => {
        if (!mountedRef.current) return;
        setSignedIn(true);
        setOwned(new Set(held.map((h) => h.ticketTypeId)));
        // A signed-in fan: resolve VIP so the storefront reflects all-access (LSC-549).
        fetchAuthz()
          .then((a) => mountedRef.current && setVipState(!!a.vip))
          .catch(() => undefined);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setSignedIn(false);
        setOwned(new Set());
        setVipState(false);
      });
  }, []);

  // Poll what's live — resilient: a failed poll leaves the last snapshot intact.
  useEffect(() => {
    if (!caveId) return;
    let active = true;
    const load = () =>
      fetchCaveLive(caveId)
        .then((l) => active && setLive(l))
        .catch(() => undefined);
    load();
    const id = window.setInterval(load, LIVE_POLL_MS);
    return () => {
      active = false;
      window.clearInterval(id);
    };
  }, [caveId]);

  const beginCheckout = async (ticketId: string) => {
    if (signedIn === false) {
      // Carry the exact ticket through sign-in so the purchase resumes on return
      // (ADR 0103), not just the page.
      onSignIn?.(ticketId);
      return;
    }
    setBusyId(ticketId);
    try {
      // Return the buyer to THIS page (cave storefront / legend profile) after
      // Stripe, not the generic root (ADR 0048).
      const res = await startTicketCheckout(ticketId, currentReturnPath());
      if (res.mode === "stripe" && res.url) {
        // Hand off to Stripe's hosted checkout; the page navigates away and the
        // webhook grants the ticket after payment. Never resolve "owned" here.
        window.location.assign(res.url);
        return;
      }
      if (!mountedRef.current) return;
      // The fan already has access — this exact type (owned) or a broader pass that
      // covers every upcoming CaveCast (covered, LSC-343). Reflect it as owned; no
      // charge, no "just acquired" confirmation.
      if (res.mode === "owned" || res.mode === "covered") {
        setOwned((s) => new Set(s).add(ticketId));
        return;
      }
      // A fresh immediate grant (free ticket, or Stripe unconfigured) — show it as
      // owned plus a clear success confirmation.
      setOwned((s) => new Set(s).add(ticketId));
      setAcquired((s) => new Set(s).add(ticketId));
      checkout.markGranted(ticketId);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 401 && mountedRef.current) {
        setSignedIn(false);
        onSignIn?.(ticketId);
      }
    } finally {
      if (mountedRef.current) setBusyId(null);
    }
  };
  const buy = (t: TicketType) => beginCheckout(t.id);
  // Retry an abandoned checkout for the same ticket (no phantom entitlement).
  const retryCheckout = () => {
    if (checkout.ticketTypeId) void beginCheckout(checkout.ticketTypeId);
  };

  // Resume a purchase the Fan started before signing in (ADR 0103): the OAuth
  // callback returns here with ?sso_buy=<ticketTypeId>. Once we know they're
  // signed in, consume it (reads + strips the param) and re-open checkout — once.
  const resumedBuyRef = useRef(false);
  useEffect(() => {
    if (signedIn !== true || resumedBuyRef.current) return;
    const id = consumeBuyIntent();
    if (id) {
      resumedBuyRef.current = true;
      void beginCheckout(id);
    }
    // beginCheckout is a stable-enough closure; we intentionally run only on the
    // signed-in transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  const signOut = () => {
    // Tear the live room down NOW so the stream stops on sign-out (ADR 0019).
    setWatching(null);
    logout().finally(() => onLogout?.());
  };

  const checkoutBanner = (
    <CheckoutReturnBanner
      status={checkout.status}
      ticketName={
        checkout.ticketTypeId ? tickets.find((t) => t.id === checkout.ticketTypeId)?.name : undefined
      }
      onRetry={retryCheckout}
      retrying={busyId != null && busyId === checkout.ticketTypeId}
      successAction={
        live.length > 0
          ? { label: "Watch now", onClick: () => setWatching(live[0].broadcastId) }
          : undefined
      }
    />
  );

  // The branded page shell (brand · back · sign-in/out + checkout banner) shared
  // by the rich storefront branches (published lobby / legacy Legend Cave).
  const pageShell = (content: ReactNode) => (
    <main
      className={watching ? "cave-public-live-page" : undefined}
      style={{
        maxWidth: "var(--cave-public-page-width, 1120px)",
        margin: "0 auto",
        padding: "18px clamp(14px, 3vw, 28px) 40px",
        minHeight: "calc(100vh / var(--ui-zoom, 1))",
      }}
    >
      <header style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0 16px" }}>
        <LegendsBrand />
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          {onBack && (
            <Button size="sm" variant="muted" onClick={onBack} aria-label="Back to legends">
              ◂ Legends
            </Button>
          )}
          {onLogout ? (
            <Button size="sm" variant="muted" onClick={signOut}>
              Sign out
            </Button>
          ) : onSignIn ? (
            <button
              onClick={() => onSignIn()}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                ...monoLabelStyle,
                color: "var(--gold)",
              }}
            >
              Sign in ▸
            </button>
          ) : null}
        </div>
      </header>
      {checkoutBanner}
      {content}
    </main>
  );

  // A PUBLISHED Cave Lobby (ADR 0130) takes over the storefront for any cave —
  // legend-headlined or not. Until an editor publishes (source "default" / no
  // lobby), we retain the legacy render below (spec §8), which also keeps the
  // existing storefront tests stable.
  // Hold an intentional loading state until we know WHICH storefront this Cave is
  // (LSC-568) — never paint the basic storefront and then swap to the authored Cave.
  const identityPending =
    !!caveId && identityTimedOut !== caveId && (lobbySettled !== caveId || legendSettled !== caveId);
  if (identityPending) {
    return pageShell(<AsyncState status="loading" loadingText="Entering the Cave…" />);
  }

  if (lobby?.source === "published" && caveId) {
    return pageShell(
      <LobbyRenderer
        doc={lobby.lobby}
        caveName={name}
        legend={legend}
        caveId={caveId}
        assets={lobby.assets}
        tickets={tickets}
        live={live}
        events={events}
        eventsStatus={eventsStatus}
        onRetryEvents={loadEvents}
        owned={owned}
        acquired={acquired}
        vip={isVip}
        signedIn={signedIn}
        busyId={busyId}
        watching={watching}
        onBuy={buy}
        onWatch={setWatching}
        onStopWatch={() => setWatching(null)}
        onSignIn={onLogout ? undefined : onSignIn}
      />,
    );
  }

  // Rich Legend Cave profile (ADR 0020) when this cave headlines a legend.
  if (legend?.profile) {
    return pageShell(
      <LegendCave
        legend={legend}
        tickets={tickets}
        live={live}
        events={events}
        eventsStatus={eventsStatus}
        onRetryEvents={loadEvents}
        owned={owned}
        acquired={acquired}
        vip={isVip}
        signedIn={signedIn}
        busyId={busyId}
        watching={watching}
        onBuy={buy}
        onWatch={setWatching}
        onStopWatch={() => setWatching(null)}
        onSignIn={onLogout ? undefined : onSignIn}
      />,
    );
  }

  return (
    <PersonaCard
      title={`${name} · Public`}
      copy="Ticket storefront for this cave."
      contentClassName={watching ? "cave-public-live-card" : undefined}
      onSignIn={onLogout ? undefined : onSignIn}
      headerRight={
        onLogout ? (
          <Button
            size="sm"
            variant="muted"
            onClick={() => {
              // Tear the live room down NOW (unmounts ConsumerWatch →
              // room.disconnect) so the stream stops on sign-out instead of
              // lingering until React happens to unmount it or the token
              // expires (ADR 0018 entitlement-durability fix).
              setWatching(null);
              logout().finally(onLogout);
            }}
          >
            Sign out
          </Button>
        ) : undefined
      }
    >
      {checkoutBanner}
      <section aria-label="Live now" style={{ marginTop: 20 }}>
        <h2 style={{ ...monoLabelStyle, margin: 0 }}>Live now</h2>
        <div style={{ marginTop: 8 }}>
          {watching ? (
            <ConsumerWatch
              broadcastId={watching}
              onClose={() => setWatching(null)}
              onGetTicket={() => {
                setWatching(null);
                ticketsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            />
          ) : live.length > 0 ? (
            live.map((b) => (
              <div key={b.broadcastId} style={rowStyle}>
                <span
                  aria-hidden
                  style={{
                    width: 7,
                    height: 7,
                    background: "var(--alert-2)",
                    borderRadius: "50%",
                    boxShadow: "var(--glow-accent)",
                  }}
                />
                <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-hi)" }}>{b.title}</span>
                <div style={{ marginLeft: "auto" }}>
                  {signedIn === false ? (
                    // Not authenticated on THIS host — sending them into Watch
                    // would 401 at the token mint (ADR 0018). Route to sign-in.
                    <Button
                      size="sm"
                      variant="accent"
                      aria-label={`Sign in to watch ${b.title}`}
                      onClick={() => onSignIn?.()}
                    >
                      Sign in to watch
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="accent"
                      aria-label={`Watch ${b.title}`}
                      onClick={() => setWatching(b.broadcastId)}
                    >
                      Watch
                    </Button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
              Nothing live right now.
            </p>
          )}
        </div>
      </section>

      {caveId && <RecordingsOnDemand caveId={caveId} signedIn={signedIn} onSignIn={onSignIn} />}

      <section aria-label="Upcoming events" style={{ marginTop: 20 }}>
        <h2 style={{ ...monoLabelStyle, margin: 0 }}>Upcoming events</h2>
        <div style={{ marginTop: 8 }}>
          {eventsStatus === "error" ? (
            // A failed events read is distinct from an empty schedule (ADR 0049).
            <AsyncState status="error" errorText="Couldn't load upcoming events." onRetry={loadEvents} compact />
          ) : eventsStatus === "loading" ? (
            <AsyncState status="loading" loadingText="Loading upcoming events…" compact />
          ) : events.length > 0 ? (
            events.map((e) => (
              <div
                key={e.id}
                style={{
                  display: "flex",
                  // Narrow phone storefronts: let the event row wrap rather than
                  // overflow the card (ADR 0111); unchanged at desktop widths.
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  gap: 10,
                  padding: "7px 0",
                  borderTop: "1px solid var(--border-faint)",
                }}
              >
                <span style={{ ...mutedMonoStyle, minWidth: 150 }}>{formatDateTime(e.startsAt)}</span>
                <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-hi)" }}>{e.title}</span>
                <span style={{ ...mutedMonoStyle, marginLeft: "auto" }}>{e.durationMinutes} MIN</span>
              </div>
            ))
          ) : (
            <AsyncState status="empty" emptyText="No upcoming events." compact />
          )}
        </div>
      </section>

      <section aria-label="Tickets" style={{ marginTop: 20 }} ref={ticketsRef}>
        <h2 style={{ ...monoLabelStyle, margin: 0 }}>Tickets</h2>
        <div style={{ marginTop: 8 }}>
          {tickets.map((t) => (
            <div
              key={t.id}
              style={{
                display: "flex",
                // Narrow phone storefronts: let the ticket row wrap rather than
                // overflow the card (ADR 0111); unchanged at desktop widths.
                flexWrap: "wrap",
                alignItems: "center",
                gap: 10,
                padding: "7px 0",
                borderTop: "1px solid var(--border-faint)",
              }}
            >
              <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-hi)" }}>{t.name}</span>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--fs-3xs)",
                  letterSpacing: "var(--ls-label)",
                  color: "var(--text-dim)",
                }}
              >
                {formatKind(t.kind)}
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--fs-2xs)",
                  letterSpacing: "var(--ls-label)",
                  color: "var(--text-hi)",
                  fontVariantNumeric: "tabular-nums",
                }}
                title={proratedNote(t) || undefined}
              >
                {isProrated(t) && (
                  <s style={{ opacity: 0.55, marginRight: 6, fontWeight: 400 }}>{formatPrice(t.priceCents)}</s>
                )}
                {formatPrice(t.proratedPriceCents)}
              </span>
              {isProrated(t) && (
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--fs-3xs)",
                    letterSpacing: "var(--ls-label)",
                    textTransform: "uppercase",
                    color: "var(--text-dim)",
                  }}
                >
                  {proratedNote(t)}
                </span>
              )}
              {owned.has(t.id) ? (
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--fs-2xs)",
                    letterSpacing: "var(--ls-label)",
                    textTransform: "uppercase",
                    color: "var(--accent)",
                  }}
                >
                  {acquired.has(t.id) ? "Ticket acquired" : "Owned"}
                </span>
              ) : signedIn === false ? (
                <Button
                  size="sm"
                  variant="muted"
                  aria-label={`Sign in to buy ${t.name}`}
                  onClick={() => onSignIn?.(t.id)}
                >
                  Sign in to buy
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="accent"
                  aria-label={`Buy ${t.name}`}
                  disabled={busyId === t.id}
                  onClick={() => buy(t)}
                >
                  {busyId === t.id ? "Acquiring…" : "Buy"}
                </Button>
              )}
            </div>
          ))}
          {tickets.length === 0 && (
            <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
              No tickets on sale.
            </p>
          )}
        </div>
      </section>
    </PersonaCard>
  );
}
